import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { media, layout, remCalc } from 'src/components/base';
import { Tooltip } from '@prisjakt/ui/tooltip';
import { zindex } from 'src/ui/zindex';
import { IconCategories } from '@prisjakt/ui/icon/categories';
import { IconClose } from '@prisjakt/ui/icon/close';
import { IconMagnifier } from '@prisjakt/ui/icon/magnifier';
import { Clickable } from '@prisjakt/ui/clickable';

type MobileHeaderProps = {
  mobileSearchOpen?: boolean;
};

const logoHeight = {
  small: '30px',
  medium: '35px',
  large: '38px',
};

export const height = {
  desktop: layout.headerHeight.desktop.rem,
  mobile: layout.headerHeight.mobile.rem,
  mobileWithSearch: layout.headerHeight.mobileWithSearch.rem,
};

const headerMaxWidth = layout.pageContent.maxWidth;

// The viewport width above which the collapsed sidebar no longer occupies the same horizontal area as the header content
const sidebarOutsideHeaderWidth =
  headerMaxWidth.value + layout.sidebarMenuWidth.desktop.value * 2;

const mobileHeaderHeight = ({ mobileSearchOpen }: MobileHeaderProps) => {
  return mobileSearchOpen
    ? layout.headerHeight.mobileWithSearch.value
    : layout.headerHeight.mobile.value;
};

type FixedMarginProps = MobileHeaderProps;
export const FixedMargin = styled.div<FixedMarginProps>(props => [
  {
    display: props.theme.layout === 'raw-mobile' ? 'none' : 'block',
    height: height.desktop,
    transition: 'height 300ms ease',
    willChange: 'height',
  },

  media.smallOnly({
    height: remCalc(mobileHeaderHeight(props)),
  }),
]);

export const HeaderWrapper = styled.header`
  position: sticky;
  height: ${height.desktop};
  z-index: ${zindex.menu.header};
`;

type HeaderBarProps = MobileHeaderProps;
export const HeaderBar = styled.header.attrs({
  'data-test': 'Header',
})<HeaderBarProps>`
  display: flex;
  justify-content: space-between;
  background: ${p => p.theme.header.bg};
  width: 100%;
  position: fixed;
  top: 0;
  height: ${height.desktop};
  /* Should be above mega menu */

  ${media.smallOnly`
    background: transparent;
    height: auto;
    max-height: ${layout.headerHeight.mobileWithSearch.rem};

    ::before {
      background: ${p => p.theme.header.bg};
      content: '';
      display: block;
      height: ${layout.headerHeight.mobile.rem};
      left: -50px;
      position: absolute;
      right: 0;
      top: 0;
      z-index: -1;
    }
  `}
`;

export const Wrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  max-width: ${headerMaxWidth.value + layout.sidebarMenuWidth.desktop.value}px;
  height: 100%;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  /* Should be above SlidingBox */
  z-index: 2;

  ${media.smallOnly`
    height: ${height.mobile};
  `}

  ${media.mediumOnly`
    justify-content: flex-start;
  `}
`;

const columnStyles = css`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: 100%;
`;

type MenuItemProps = {
  wideOnTablet?: boolean;
};
export const MenuItem = styled.div<MenuItemProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 50px;
  height: 100%;
  ${props =>
    props.wideOnTablet &&
    media.mediumOnly`
    width: 100px;
  `}
`;

export const MenuColumn = styled.div`
  ${columnStyles};
  width: auto;

  ${media.largeUp`
    display: none;
  `};
`;

export const MegaMenuItem = styled(Tooltip)`
  height: ${height.desktop};
  flex-grow: 0;

  ${media.mediumDown`
    display: none;
  `}
`;

type MegaMenuButtonProps = {
  active?: boolean;
};
export const MegaMenuButton = styled.button<MegaMenuButtonProps>`
  cursor: pointer;
  display: block;
  height: 100%;
  width: ${layout.sidebarMenuWidth.desktop.rem};
  background: ${p => (p.active ? 'white' : 'transparent')};
  color: ${p => (p.active ? p.theme.icons.callToAction : p.theme.header.fg)};
  transition: background-color 0.1s linear, color 0.1s linear;

  &:hover,
  &.focus-visible {
    background: ${p => (p.active ? 'white' : p.theme.buttons.primary.bg)};
  }

  /* Double selector to override aggressive user-agent styles in e.g. Firefox */
  &:focus:focus {
    outline: none;
  }
`;

export const MegaMenuIcon = styled(IconCategories).attrs(p => ({
  color: p.theme.header.fg,
}))`
  fill: currentColor;
`;

export const CloseMegaMenuIcon = styled(IconClose).attrs(p => ({
  color: p.theme.colors.maybeBlue700,
}))`
  fill: currentColor;
`;

export const LogoColumn = styled.div`
  ${columnStyles}

  /* Special case for a certain interval of viewport widths where we set this whole column as fixed position and aligned to the left.
     This is because we want the mega menu button to align with the sidebar, but it also has to play nicely with the logo when they "collide".
     See also the Wrapper component which adds a pseudo-element at this same interval to reserve space for the column. */
  @media screen and (min-width: ${headerMaxWidth.value}px) and (max-width: ${sidebarOutsideHeaderWidth}px) {
    left: 0;
    height: ${height.desktop};
  }

  ${media.mediumOnly`
    max-width: 200px;
  `};

  ${media.smallOnly`
    height: ${height.mobile};
    width: auto;
  `}
`;

export const LogoLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 0 10px;

  ${media.showOutsiderAd`
    margin-left: 0;
  `};

  img {
    height: ${logoHeight.small};

    ${media.mediumUp`
      height: ${logoHeight.medium};
    `};

    ${media.largeUp`
      height: ${logoHeight.large};
    `};
  }

  &.focus-visible {
    background: ${p => p.theme.buttons.primary.bg};
    outline: none;
  }
`;

export const RightColumn = styled.div`
  ${columnStyles}
  width: auto;

  ${media.smallOnly`
    flex: 0 0 50px;
    width: auto;
  `}

  ${media.mediumOnly`
    flex-grow: 1;
  `}

  ${media.mediumDown`
    justify-content: flex-end;
  `}
`;

export const PromoText = styled.div(
  props => css`
    ${media.mediumDown`
      display: none;
    `}

    display: flex;
    align-items: center;
    color: ${props.theme.colors.base0};
    margin-left: ${layout.measurements.spacing200};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.4px;
    max-width: 120px;
    margin-right: ${layout.measurements.spacing050};
  `
);

export const SearchIcon = styled(IconMagnifier).attrs(p => ({
  color: p.theme.header.fg,
}))`
  color: ${p => p.theme.header.fg};
  fill: currentColor;
`;

export const UserArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: ${layout.measurements.spacing100};
  margin-left: auto;
  position: relative;

  > :not(:last-child) {
    margin-right: ${layout.measurements.spacing100};
  }

  ${media.mediumDown`
    display: none;
  `};
`;

export const MagnifyerButton = styled(Clickable)``;

export const SearchBarWrapper = styled.div<{ $visible: boolean }>`
  ${media.smallOnly<{ $visible: boolean }>`
    height: 0;
    width: 0;
    overflow: hidden;
    position: absolute;
  `}
  ${p => (!p.$visible ? 'display: none;' : 'display: flex;')}
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  margin: 0 50px;
`;
